import { useEffect, useRef, useState } from "react";
import { Send, SmilePlus, Flag, ChevronDown, Reply, X, Pin, Check, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import ViewersList from "@/components/ViewersList";
import { containsPiracyLink } from "@/lib/legal";
import { FormattedText, Avatar, timeLabel, fullTimestamp, formatClock } from "@/lib/chatFormat";

const QUICK_REACTIONS = ["❤️", "😂", "🔥", "👍", "😍", "🎉", "🍿", "💀", "🤯", "⚡"];
const PICKER_EMOJIS = ["❤️", "😂", "🔥", "👍", "😍", "🎉", "🍿", "💀", "🤯", "⚡", "😮", "😢", "👏", "🙌", "💯", "🥳", "😎", "🤔", "😱", "🫶", "✨", "🎬", "🍻", "🤩"];
const RECENT_KEY = "ibeflix_recent_emoji";

function getRecent() {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch (e) { return []; }
}
function pushRecent(emoji) {
  try {
    const cur = getRecent().filter((e) => e !== emoji);
    localStorage.setItem(RECENT_KEY, JSON.stringify([emoji, ...cur].slice(0, 8)));
  } catch (e) { /* storage blocked */ }
}

function ReactionChips({ reactions, myName, onToggle }) {
  const entries = Object.entries(reactions || {}).filter(([, users]) => (users || []).length > 0);
  if (entries.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1 px-1" data-testid="message-reactions">
      {entries.map(([emoji, users]) => {
        const mine = (users || []).includes(myName);
        return (
          <button
            key={emoji}
            data-testid={`message-reaction-chip-${emoji}`}
            onClick={() => onToggle(emoji)}
            title={(users || []).join(", ")}
            className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs transition-colors active:scale-90 ${
              mine ? "bg-[#00D9FF]/20 border border-[#00D9FF]/50 text-white" : "bg-white/5 border border-white/10 text-[#A0A0AB] hover:bg-white/10"
            }`}
          >
            <span>{emoji}</span>
            <span className="font-semibold">{(users || []).length}</span>
          </button>
        );
      })}
    </div>
  );
}

function ReactionPicker({ open, onPick, isSelf }) {
  const [expanded, setExpanded] = useState(false);
  const recent = getRecent();
  if (!open) return null;
  const base = expanded ? PICKER_EMOJIS : PICKER_EMOJIS.slice(0, 12);
  return (
    <div
      data-testid="reaction-picker"
      onClick={(e) => e.stopPropagation()}
      className={`absolute -top-2 -translate-y-full z-30 bg-[#141c40] border border-white/10 rounded-2xl p-2 shadow-xl animate-msg w-[248px] ${isSelf ? "right-0" : "left-0"}`}
    >
      {recent.length > 0 && (
        <>
          <p className="text-[10px] text-[#71717A] font-semibold uppercase tracking-wider px-1 mb-1">Recent</p>
          <div className="flex flex-wrap gap-0.5 mb-1.5 pb-1.5 border-b border-white/10">
            {recent.map((e) => (
              <button key={`r-${e}`} data-testid={`message-react-recent-${e}`} onClick={() => onPick(e)} className="w-7 h-7 rounded-full hover:bg-white/10 transition-colors text-base flex items-center justify-center active:scale-90">{e}</button>
            ))}
          </div>
        </>
      )}
      <div className="flex flex-wrap gap-0.5">
        {base.map((e) => (
          <button key={e} data-testid={`message-react-${e}`} onClick={() => onPick(e)} className="w-7 h-7 rounded-full hover:bg-white/10 transition-colors text-base flex items-center justify-center active:scale-90">{e}</button>
        ))}
        {!expanded && (
          <button data-testid="reaction-picker-expand" onClick={() => setExpanded(true)} title="More emojis" className="w-7 h-7 rounded-full bg-white/5 hover:bg-white/15 transition-colors text-sm font-bold flex items-center justify-center text-[#A0A0AB]">+</button>
        )}
      </div>
    </div>
  );
}

function ReadReceipt({ msg, me, reads, viewersCount }) {
  if (msg.isHost || msg.name !== me.name || !msg.timestamp) return null;
  const mt = new Date(msg.timestamp).getTime();
  const readers = (reads || []).filter((r) => r.name !== me.name && new Date(r.at).getTime() >= mt);
  const othersPresent = (viewersCount || 0) > 1;
  let icon;
  let title;
  if (readers.length > 0) {
    icon = <CheckCheck data-testid="receipt-read" className="w-3.5 h-3.5 text-[#FF006E]" style={{ filter: "drop-shadow(0 0 4px rgba(255,0,110,0.8))" }} />;
    title = readers.map((r) => `${r.name} read at ${formatClock(r.at)}`).join("\n");
  } else if (othersPresent) {
    icon = <CheckCheck data-testid="receipt-delivered" className="w-3.5 h-3.5 text-[#00D9FF]/70" />;
    title = "Delivered";
  } else {
    icon = <Check data-testid="receipt-sent" className="w-3.5 h-3.5 text-[#71717A]" />;
    title = "Sent";
  }
  return (
    <span data-testid="chat-receipt" title={title} className="inline-flex items-center ml-1 align-middle">{icon}</span>
  );
}

function ReplyQuote({ reply, isSelf }) {
  if (!reply) return null;
  return (
    <div
      data-testid="reply-quote"
      className={`mb-1 max-w-[85%] border-l-2 pl-2 py-1 rounded-r-md text-[11px] ${isSelf ? "border-white/40 bg-white/5" : "border-[#00D9FF]/60 bg-[#00D9FF]/5"}`}
    >
      <span className="font-semibold text-[#E4E4E7]">↩ {reply.name}</span>
      <span className="text-[#A0A0AB] ml-1 line-clamp-1">{reply.text}</span>
    </div>
  );
}

function MessageBubble({ msg, me, nowMs, mentionNames, onMsgReact, onReport, onReply, onProfile, onPin, reads, viewersCount }) {
  const myLang = me.language;
  const isSelf = !msg.isHost && msg.name === me.name;
  const [pickerOpen, setPickerOpen] = useState(false);
  const pressTimer = useRef(null);

  const pick = (emoji) => { onMsgReact && onMsgReact(msg.id, emoji); pushRecent(emoji); setPickerOpen(false); };
  const toggleReaction = (emoji) => onMsgReact && onMsgReact(msg.id, emoji);
  const touchStart = useRef(null);
  const onTouchStart = (e) => {
    const t = e.touches ? e.touches[0] : e;
    touchStart.current = { x: t.clientX, y: t.clientY };
    pressTimer.current = setTimeout(() => setPickerOpen(true), 450);
  };
  const onTouchMove = (e) => {
    const s = touchStart.current;
    if (!s) return;
    const t = e.touches ? e.touches[0] : e;
    if (Math.abs(t.clientX - s.x) > 8 || Math.abs(t.clientY - s.y) > 8) clearTimeout(pressTimer.current);
  };
  const onTouchEnd = (e) => {
    clearTimeout(pressTimer.current);
    const s = touchStart.current;
    touchStart.current = null;
    const t = e.changedTouches && e.changedTouches[0];
    if (!s || !t) return;
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) { onReply && onReply(msg); }   // swipe left → reply
      else { setPickerOpen(true); }              // swipe right → quick-react tray
    }
  };

  useEffect(() => {
    if (!pickerOpen) return undefined;
    const h = () => setPickerOpen(false);
    window.addEventListener("click", h);
    return () => window.removeEventListener("click", h);
  }, [pickerOpen]);

  let main = msg.text;
  let original = null;
  const translated = msg.translations && msg.translations[myLang];
  if (msg.language !== myLang && translated) {
    main = translated;
    original = msg.text;
  }
  const pending = msg.language !== myLang && !translated;
  const stamp = <span className="text-[10px] text-[#9D4EDD]" style={{ textShadow: "0 0 6px rgba(157,78,221,0.5)" }} title={fullTimestamp(msg.timestamp)} data-testid="chat-timestamp">{timeLabel(msg.timestamp, nowMs)}</span>;

  if (msg.isHost) {
    return (
      <div className="animate-msg" data-testid="chat-message-host">
        <div className="bg-[#00D9FF]/10 border-l-2 border-[#00D9FF] p-3 rounded-r-xl rounded-bl-xl">
          <div className="flex items-center gap-2 mb-1">
            <Avatar msg={msg} size={22} />
            <span className="text-xs font-bold text-[#00D9FF] uppercase tracking-wide">IBEFLIX Host</span>
            <span className="text-[10px] text-[#71717A] bg-white/5 px-1.5 py-0.5 rounded">AI</span>
            {msg.timestamp && stamp}
          </div>
          <p className="text-sm text-[#F1F1F5] leading-relaxed"><FormattedText text={main} mentionNames={mentionNames} myName={me.name} /></p>
          {original && (
            <p className="text-[11px] text-[#71717A] mt-1 italic">“{original}”</p>
          )}
        </div>
        <ReactionChips reactions={msg.reactions} myName={me.name} onToggle={toggleReaction} />
      </div>
    );
  }

  return (
    <div className={`animate-msg flex flex-col ${isSelf ? "items-end" : "items-start"}`} data-testid="chat-message">
      <div className={`flex items-center gap-1.5 mb-1 px-1 ${isSelf ? "flex-row-reverse" : ""}`}>
        <Avatar msg={msg} size={26} onClick={isSelf ? undefined : () => onProfile && onProfile(msg)} />
        <span className="text-xs font-semibold text-[#A0A0AB]">{isSelf ? "You" : msg.name}</span>
        {stamp}
        <ReadReceipt msg={msg} me={me} reads={reads} viewersCount={viewersCount} />
      </div>
      <ReplyQuote reply={msg.reply} isSelf={isSelf} />
      <div
        className="group relative max-w-[85%]"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onTouchMove={onTouchMove}
      >
        <ReactionPicker open={pickerOpen} onPick={pick} isSelf={isSelf} />
        <button
          data-testid="message-react-trigger"
          onClick={(e) => { e.stopPropagation(); setPickerOpen((v) => !v); }}
          className={`absolute top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity w-7 h-7 rounded-full bg-[#141c40] border border-white/10 flex items-center justify-center text-[#A0A0AB] hover:text-white ${isSelf ? "-left-9" : "-right-9"}`}
        >
          <SmilePlus className="w-3.5 h-3.5" />
        </button>
        <button
          data-testid="message-reply-trigger"
          title="Reply"
          onClick={(e) => { e.stopPropagation(); onReply && onReply(msg); }}
          className={`absolute top-1/2 -translate-y-9 opacity-0 group-hover:opacity-100 transition-opacity w-7 h-7 rounded-full bg-[#141c40] border border-white/10 flex items-center justify-center text-[#A0A0AB] hover:text-white ${isSelf ? "-left-9" : "-right-9"}`}
        >
          <Reply className="w-3.5 h-3.5" />
        </button>
        <button
          data-testid="message-pin-trigger"
          title="Pin message"
          onClick={(e) => { e.stopPropagation(); onPin && onPin(msg); }}
          className={`absolute top-1/2 translate-y-7 opacity-0 group-hover:opacity-100 transition-opacity w-7 h-7 rounded-full bg-[#141c40] border border-white/10 flex items-center justify-center text-[#A0A0AB] hover:text-[#00D9FF] ${isSelf ? "-left-9" : "-right-9"}`}
        >
          <Pin className="w-3.5 h-3.5" />
        </button>
        {!isSelf && onReport && (
          <button
            data-testid="message-report-trigger"
            title="Report this message"
            onClick={(e) => { e.stopPropagation(); onReport(msg); }}
            className={`absolute top-1/2 translate-y-[3.75rem] opacity-0 group-hover:opacity-100 transition-opacity w-7 h-7 rounded-full bg-[#141c40] border border-white/10 flex items-center justify-center text-[#A0A0AB] hover:text-[#00D9FF] ${isSelf ? "-left-9" : "-right-9"}`}
          >
            <Flag className="w-3 h-3" />
          </button>
        )}
        <div
          className={`p-3 text-sm leading-relaxed break-words ${
            isSelf
              ? "bg-[#00D9FF] text-navy-ink rounded-2xl rounded-tr-sm shadow-[0_0_18px_rgba(0,217,255,0.4)]"
              : "bg-white/[0.06] text-[#F0F6FF] rounded-2xl rounded-tl-sm border border-[#9D4EDD]/20"
          }`}
        >
          <p><FormattedText text={main} mentionNames={mentionNames} myName={me.name} /></p>
          {containsPiracyLink(msg.text) && (
            <p data-testid="piracy-warning" className="text-[11px] mt-1.5 flex items-center gap-1 text-amber-300">
              <Flag className="w-3 h-3" /> Possible pirated link — this violates our Terms.
            </p>
          )}
          {original && (
            <p className={`text-[11px] mt-1.5 pt-1.5 border-t italic ${isSelf ? "border-white/20 text-white/70" : "border-white/10 text-[#71717A]"}`}>
              <span className="not-italic font-semibold mr-1">orig:</span>{original}
            </p>
          )}
          {pending && (
            <p className={`text-[11px] mt-1 ${isSelf ? "text-white/60" : "text-[#71717A]"}`}>translating…</p>
          )}
        </div>
      </div>
      <ReactionChips reactions={msg.reactions} myName={me.name} onToggle={toggleReaction} />
    </div>
  );
}

function SystemLine({ msg }) {
  return (
    <div className="flex justify-center animate-msg" data-testid="chat-system-line">
      <span className="text-[11px] text-[#71717A] bg-white/5 rounded-full px-3 py-1">
        {msg.flag ? `${msg.flag} ` : ""}{msg.text}
      </span>
    </div>
  );
}

export default function ChatPanel({ viewers, messages, me, onSend, onReact, onMsgReact, onTyping, typingUsers = [], drivers = [], myId, canManage, onInviteCoDriver, onRemoveDriver, voiceIds, reads = [], pins = [], onPin, onUnpin, onReport, scrollToBottomRef, onAtBottomChange }) {
  const [text, setText] = useState("");
  const scrollRef = useRef(null);
  const typingRef = useRef(false);
  const typingTimeout = useRef(null);
  const atBottomRef = useRef(true);
  const [showJump, setShowJump] = useState(false);
  const [nowMs, setNowMs] = useState(Date.now());
  const [replyingTo, setReplyingTo] = useState(null);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionMatches, setMentionMatches] = useState([]);

  // tick relative timestamps ("2 mins ago") without touching the socket
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  const mentionNames = (viewers || []).map((v) => v.name);

  const onProfile = (msg) => {
    toast(`${msg.avatarEmoji || msg.flag || "🙂"}  ${msg.name}`, {
      description: msg.country ? `From ${msg.country} · speaks ${msg.language || "—"}` : undefined,
    });
  };

  const scrollToBottom = () => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    atBottomRef.current = true;
    setShowJump(false);
    onAtBottomChange && onAtBottomChange(true);
  };

  useEffect(() => {
    if (scrollToBottomRef) scrollToBottomRef.current = scrollToBottom;
  });

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    const atBottom = dist < 60;
    atBottomRef.current = atBottom;
    onAtBottomChange && onAtBottomChange(atBottom);
    if (atBottom) setShowJump(false);
  };

  useEffect(() => {
    if (atBottomRef.current) {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    } else {
      setShowJump(true);
    }
  }, [messages, typingUsers]);

  const emitTyping = (v) => {
    const value = v ?? text;
    if (!onTyping) return;
    if (value && !typingRef.current) {
      typingRef.current = true;
      onTyping(true);
    }
    clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(() => {
      typingRef.current = false;
      onTyping(false);
    }, 2500);
  };

  const stopTyping = () => {
    clearTimeout(typingTimeout.current);
    if (typingRef.current) {
      typingRef.current = false;
      onTyping && onTyping(false);
    }
  };

  const onChangeText = (val) => {
    setText(val);
    emitTyping(val);
    const m = val.match(/(?:^|\s)@(\w*)$/);
    if (m) {
      const q = m[1].toLowerCase();
      const matches = (viewers || []).filter((v) => v.name && v.name.toLowerCase().startsWith(q)).slice(0, 5);
      setMentionMatches(matches);
      setMentionOpen(matches.length > 0);
    } else {
      setMentionOpen(false);
    }
  };

  const pickMention = (name) => {
    setText((t) => t.replace(/@(\w*)$/, `@${name} `));
    setMentionOpen(false);
  };

  const send = () => {
    const t = text.trim();
    if (!t) return;
    onSend(t, replyingTo ? { id: replyingTo.id, name: replyingTo.name, text: replyingTo.text } : undefined);
    setText("");
    setReplyingTo(null);
    setMentionOpen(false);
    stopTyping();
    atBottomRef.current = true;
    onAtBottomChange && onAtBottomChange(true);
    setShowJump(false);
  };

  const typingLabel = typingUsers.length === 1
    ? `${typingUsers[0]} is typing…`
    : typingUsers.length === 2
      ? `${typingUsers[0]} and ${typingUsers[1]} are typing…`
      : typingUsers.length > 2
        ? `${typingUsers.length} people are typing…`
        : "";

  return (
    <div className="h-full flex flex-col border-l border-white/5 bg-[#0A0E27]/95 backdrop-blur-xl relative z-10 min-h-0">
      <ViewersList viewers={viewers} drivers={drivers} myId={myId} canManage={canManage} onInviteCoDriver={onInviteCoDriver} onRemoveDriver={onRemoveDriver} voiceIds={voiceIds} />

      {pins.length > 0 && (
        <div data-testid="pinned-bar" className="shrink-0 border-b border-white/5 bg-[#0A0E27] px-3 py-2 space-y-1">
          {pins.map((p) => (
            <div key={p.id} data-testid={`pinned-${p.id}`} className="flex items-center gap-2 text-xs">
              <Pin className="w-3 h-3 text-[#00D9FF] shrink-0" />
              <span className="text-[#A0A0AB] truncate flex-1">
                <span className="text-[#F1F1F5] font-semibold">{p.by} pinned</span>
                <span className="text-[#71717A]"> · {p.name}: </span>{p.text}
              </span>
              <button data-testid={`unpin-${p.id}`} onClick={() => onUnpin && onUnpin(p.id)} title="Unpin" className="shrink-0 w-5 h-5 rounded-full hover:bg-white/10 flex items-center justify-center text-[#71717A] hover:text-white">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div ref={scrollRef} onScroll={handleScroll} className="flex-1 overflow-y-auto chat-scroll p-4 space-y-4 min-h-0" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center text-[#71717A] gap-2">
            <span className="text-3xl">🍿</span>
            <p className="text-sm">Say hi! Type <span className="text-[#00D9FF] font-semibold">@host</span> to talk to the AI party host.</p>
          </div>
        )}
        {messages.map((m) => (
          m.isSystem
            ? <SystemLine key={m.id} msg={m} />
            : <MessageBubble key={m.id} msg={m} me={me} nowMs={nowMs} mentionNames={mentionNames} onMsgReact={onMsgReact} onReport={onReport} onReply={(mm) => setReplyingTo({ id: mm.id, name: mm.name, text: mm.text })} onProfile={onProfile} onPin={onPin} reads={reads} viewersCount={viewers.length} />
        ))}
      </div>

      <div className="shrink-0 border-t border-white/5 p-3 bg-[#060817] relative">
        {showJump && (
          <button
            data-testid="chat-jump-latest"
            onClick={scrollToBottom}
            className="absolute left-1/2 -translate-x-1/2 -top-11 z-20 flex items-center gap-1.5 bg-[#00D9FF] text-navy-ink text-xs font-bold rounded-full px-3.5 py-1.5 glow-cyan animate-msg active:scale-90 hover:bg-[#33E4FF] transition-colors"
          >
            New messages <ChevronDown className="w-3.5 h-3.5" />
          </button>
        )}
        <div className="h-5 px-1 mb-1 flex items-center" data-testid="typing-indicator">
          {typingLabel && (
            <span className="text-xs text-[#00D9FF] flex items-center gap-1.5 animate-msg">
              <span className="flex gap-0.5">
                <span className="w-1 h-1 rounded-full bg-[#00D9FF] animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1 h-1 rounded-full bg-[#00D9FF] animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-1 h-1 rounded-full bg-[#00D9FF] animate-bounce" style={{ animationDelay: "300ms" }} />
              </span>
              {typingLabel}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5 mb-2.5">
          {QUICK_REACTIONS.map((e) => (
            <button
              key={e}
              data-testid={`reaction-${e}`}
              onClick={() => onReact(e)}
              className="w-9 h-9 rounded-full bg-white/5 hover:bg-[#00D9FF]/20 transition-colors text-lg flex items-center justify-center active:scale-90 hover:-translate-y-[1px] duration-150"
            >
              {e}
            </button>
          ))}
        </div>
        {replyingTo && (
          <div data-testid="reply-preview" className="flex items-center gap-2 mb-2 px-3 py-1.5 rounded-lg bg-[#0F1636] border-l-2 border-[#00D9FF]">
            <div className="flex-1 min-w-0 text-xs">
              <span className="text-[#00D9FF] font-semibold">Replying to {replyingTo.name}</span>
              <span className="text-[#71717A] ml-1.5 line-clamp-1">{replyingTo.text}</span>
            </div>
            <button data-testid="reply-cancel" onClick={() => setReplyingTo(null)} className="shrink-0 w-6 h-6 rounded-full hover:bg-white/10 flex items-center justify-center text-[#A0A0AB] hover:text-white">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        <div className="flex gap-2 items-center relative">
          {mentionOpen && (
            <div data-testid="mention-dropdown" className="absolute bottom-12 left-0 z-30 w-56 bg-[#0F1636] border border-white/10 rounded-2xl p-1.5 shadow-2xl">
              {mentionMatches.map((v) => (
                <button
                  key={v.id}
                  data-testid={`mention-option-${v.name}`}
                  onClick={() => pickMention(v.name)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-xl hover:bg-white/5 transition-colors text-left"
                >
                  <span className="text-base">{v.avatarEmoji || v.flag}</span>
                  <span className="text-sm text-[#F1F1F5] font-medium truncate">{v.name}</span>
                </button>
              ))}
            </div>
          )}
          <input
            data-testid="chat-input"
            value={text}
            onChange={(e) => onChangeText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") send(); if (e.key === "Escape") { setMentionOpen(false); setReplyingTo(null); } }}
            placeholder="Message — **bold**, *italic*, `code`, @name"
            className="flex-1 h-11 bg-[#0F1636] border border-white/10 rounded-full px-4 text-sm text-[#F1F1F5] outline-none focus:border-[#00D9FF] transition-colors"
          />
          <button
            data-testid="chat-send-button"
            onClick={send}
            className="btn-neon glow-pink w-11 h-11 shrink-0 rounded-full bg-[#FF006E] hover:bg-[#FF3388] transition-colors flex items-center justify-center active:scale-90"
          >
            <Send className="w-4 h-4 text-white" />
          </button>
        </div>
      </div>
    </div>
  );
}
